import { API_URL } from "@/config/constants";
import { buildUrl } from "@/utils";
import axios from "axios";
import { IAdminScopeJobRun } from "../admin/admin.types";
import { IFilter, Page } from "../api.types";
import { IClause } from "../clauses";
import {
    IClauseComment,
    IClauseCommentCreate,
} from "../comments/comments.types";
import { IFeedback, IFeedbackCreate } from "../feedback";
import { ITaskIdentifier } from "../tasks";
import { IDocumentExtractedFieldsResponse } from "../documents/documents.types";
import {
    IExtractedCertificateOfInsurance,
    IExportBase,
    IScope,
    IScopeClause,
    IScopeCreate,
    IScopeOverview,
    IScopeReportRequest,
    IScopeStatus,
    IScopeTopicsWithClauses,
    IScopeUpdate,
    ISignedScopeDocument,
    IExtractedPropertyUpdate,
    IBatchDeleteRequest,
    IBatchDeleteResponse,
} from "./scopes.types";

const API = `${API_URL}/scopes`;

export const createScope = async (scopeCreate: IScopeCreate) => {
    const result = await axios.post<ITaskIdentifier>(API, scopeCreate);
    return result.data;
};

export const getScopes = async (
    page: number = 1,
    size: number = 50,
    filter?: IFilter,
    search?: string | null
) => {
    const url = buildUrl(API, { page, size, search, ...filter });

    const result = await axios.get<Page<IScope>>(url);
    return result.data;
};

export const getClausesByScopeIds = async (scopeIds: number[]) => {
    const params = scopeIds.map((id) => `scope_ids=${id}`).join("&");
    const result = await axios.get<IClause[]>(`${API}/clauses?${params}`);
    return result.data;
};

export const getScope = async (id: number) => {
    const result = await axios.get<IScope>(`${API}/${id}`);
    return result.data;
};

export const updateScope = async (id: number, scopeUpdate: IScopeUpdate) => {
    const result = await axios.put<IScope>(`${API}/${id}`, scopeUpdate);
    return result.data;
};

export const deleteScope = async (id: number) => {
    await axios.delete(`${API}/${id}`);
};

export const getScopeTopicsWithClauses = async (id: number) => {
    const result = await axios.get<IScopeTopicsWithClauses>(
        `${API}/${id}/topics-with-clauses`
    );
    return result.data;
};

export const getScopeClause = async (id: number, clauseId: number) => {
    const result = await axios.get<IScopeClause>(
        `${API}/${id}/clauses/${clauseId}`
    );
    return result.data;
};

export const updateScopeClause = async (
    id: number,
    clause_id: number,
    guidance: string
) => {
    const result = await axios.put<IScopeClause>(
        `${API}/${id}/clauses/${clause_id}`,
        { guidance }
    );
    return result.data;
};

export const createScopeClauseFeedback = async (
    id: number,
    clause_id: number,
    feedbackCreate: IFeedbackCreate
) => {
    const result = await axios.post<IFeedback>(
        `${API}/${id}/clauses/${clause_id}/feedback`,
        feedbackCreate
    );
    return result.data;
};

export const getScopeClauseFeedback = async (id: number, clauseId: number) => {
    const result = await axios.get<IFeedback>(
        `${API}/${id}/clauses/${clauseId}/feedback`
    );
    return result.data;
};

export const getSignedScopeDocument = async (id: number) => {
    const result = await axios.get<ISignedScopeDocument>(
        `${API}/${id}/document`
    );
    return result.data;
};

// Create a new axios instance for downloading the signed document
// This is necessary because Bunny doesn't accept an Authorization header
const downloadAxios = axios.create();

export const downloadSignedScopeDocument = async (id: number) => {
    const signedDocumentResponse = await getSignedScopeDocument(id);

    const response = await downloadAxios.get(
        signedDocumentResponse.signed_url,
        {
            responseType: "blob",
            headers: {},
        }
    );

    // return the blob
    return response.data;
};

export const exportScopes = async (
    scopeIds: number[],
    exportData: IExportBase
) => {
    const clauseParams = exportData.clauses
        .map((id) => `clauses=${id}`)
        .join("&");

    const scopeParams = scopeIds.map((id) => `scope_ids=${id}`).join("&");

    const url = buildUrl(`${API}/export`, {
        export_type: exportData.export_type,
        include_guidance: exportData.include_guidance,
        include_metadata: exportData.include_metadata,
        include_comments: exportData.include_comments,
    });

    const result = await axios.get(`${url}&${clauseParams}&${scopeParams}`, {
        responseType: "blob",
    });

    return result.data;
};

export const createClauseComment = async (
    scope_id: number,
    clause_id: number,
    clauseCreate: IClauseCommentCreate
) => {
    const result = await axios.post<IClauseCommentCreate>(
        `${API}/${scope_id}/clauses/${clause_id}/comments`,
        clauseCreate
    );
    return result.data;
};

export const getCommentsByClauseId = async (
    scope_id: number,
    clause_id: number
) => {
    const result = await axios.get<Page<IClauseComment>>(
        `${API}/${scope_id}/clauses/${clause_id}/comments`
    );
    return result.data;
};

export const getScopeStatus = async (id: number) => {
    const result = await axios.get<IScopeStatus>(`${API}/${id}/status`);
    return result.data;
};

export const getScopeCertificateOfInsurance = async (id: number) => {
    const result = await axios.get<IExtractedCertificateOfInsurance | null>(
        `${API}/${id}/coi`
    );
    return result.data;
};

export const generateScopeReport = async (
    id: number,
    reportParams: IScopeReportRequest
) => {
    const clauseParams = reportParams.clauses
        .map((id) => `clauses=${id}`)
        .join("&");
    const url = buildUrl(`${API}/${id}/report`, {
        include_guidance: reportParams.include_guidance,
        include_comments: reportParams.include_comments,
        include_metadata: reportParams.include_metadata,
    });

    const result = await axios.get(`${url}&${clauseParams}`, {
        responseType: "blob",
    });

    return result.data;
};

export const getScopeExtractedFields = async (id: number) => {
    const result = await axios.get<IDocumentExtractedFieldsResponse>(
        `${API}/${id}/extracted-fields`
    );
    return result.data;
};

export const getScopeOverview = async (id: number) => {
    const result = await axios.get<IScopeOverview>(`${API}/${id}/overview`);
    return result.data;
};

export const getScopeJobRun = async (scopeId: number) => {
    const result = await axios.get<IAdminScopeJobRun>(
        `${API}/${scopeId}/job-run`
    );
    return result.data;
};

export const rerunScope = async (scopeId: number) => {
    const result = await axios.post<{ message: string; scope_id: number }>(
        `${API}/${scopeId}/re-run`
    );
    return result.data;
};

// Extracted property mutations

export const updateExtractedProperty = async (
    scopeId: number,
    propertyId: number,
    data: IExtractedPropertyUpdate
) => {
    const result = await axios.put(
        `${API}/${scopeId}/extracted-properties/${propertyId}`,
        data
    );
    return result.data;
};

export const deleteExtractedProperty = async (
    scopeId: number,
    propertyId: number
) => {
    await axios.delete(`${API}/${scopeId}/extracted-properties/${propertyId}`);
};

export const batchDeleteExtractedProperties = async (
    scopeId: number,
    data: IBatchDeleteRequest
) => {
    const result = await axios.post<IBatchDeleteResponse>(
        `${API}/${scopeId}/extracted-properties/batch-delete`,
        data
    );
    return result.data;
};
